/**
 * Campaign status as the design's five tones.
 *
 * The module carries its own `StatusPill` in `shared.tsx` with a hand-rolled
 * colour map, which is still what the campaign detail screens use. The list page
 * uses the CRM's pill, so it needs the same five statuses said in the CRM's
 * vocabulary rather than in emerald-500 and amber-500.
 */
import type { StatusTone } from "../../ui";
import type { CampaignStatus } from "../../../lib/marketing";

/**
 * ok = running, info = waiting for its start time, wt = stopped by a person,
 * mut = not started or finished.
 *
 * `done` is `mut` rather than `ok` deliberately: a finished campaign is history,
 * and giving it the same green as a running one makes the list look like far
 * more is in flight than is.
 */
const TONE: Record<CampaignStatus, StatusTone> = {
  draft: "mut",
  scheduled: "info",
  active: "ok",
  paused: "wt",
  done: "mut",
};

export function campaignTone(s: CampaignStatus): StatusTone {
  return TONE[s] ?? "mut";
}

/**
 * A LEDGER ROW's standing, as opposed to a campaign's or a mailbox's.
 *
 * ok = it arrived or came back to us, lt = it did not, wt = the recipient
 * stepped away, mut = nothing has happened to this row yet.
 *
 * `skipped` is `mut` rather than a warning: most skips are the audience rules
 * working exactly as configured, and colouring 900 correct decisions as faults
 * would make the handful of real failures impossible to find.
 *
 * Nine values, because `MktRecipient.status` has nine. `sending` is a transient
 * lease and not an event - a row can sit in it because a run crashed - so it is
 * the same quiet grey as `pending` rather than a colour suggesting progress.
 * The map lives here rather than beside one panel because two screens now draw
 * these rows, and a second copy is what drifts the day a status is added.
 */
const RECIPIENT_TONE: Record<string, StatusTone> = {
  pending: "mut",
  sending: "mut",
  sent: "ok",
  replied: "ok",
  skipped: "mut",
  failed: "lt",
  bounced: "lt",
  // Not `wt` beside `unsubscribed`. An opt-out is a reader using the door we
  // gave them; a spam report is a reader telling their provider we should not
  // have written, and it is the single most expensive thing on this list.
  complained: "lt",
  unsubscribed: "wt",
};

export function recipientTone(s: string): StatusTone {
  return RECIPIENT_TONE[s] ?? "mut";
}

/**
 * A MAILBOX's standing. `MktContact.status`, which outlives every campaign.
 *
 * `active` is `mut` and not `ok`: an address in the ledger that nothing has
 * happened to is the ordinary case, and painting 800 rows green would leave no
 * colour left for the ones that matter. The three that stop future sending get
 * the tones the ledger uses for the same words, so an address that complained
 * looks the same here as the letter that provoked it.
 */
const CONTACT_TONE: Record<string, StatusTone> = {
  active: "mut",
  unsubscribed: "wt",
  bounced: "lt",
  complained: "lt",
  blocked: "lt",
};

export function contactTone(s: string): StatusTone {
  return CONTACT_TONE[s] ?? "mut";
}
